import { HttpClient, httpResource } from '@angular/common/http';
import { inject, Injectable, Signal } from '@angular/core';
import {
    ConvertAuthorizedAmountRequest,
    ConvertAuthorizedAmountResponse,
    DuplicateCheckResultDto,
} from '@app/generated-api';
import { BatchSetAddExpense, BatchSetAddResponse } from '@models/batch-set-add-expense';
import { BatchEditSetDataRow } from '@models/batchEditSetDataRow';
import { CountryRegionCtsmViewModel } from '@models/clintrak';
import { CurrencyViewModel } from '@models/currency';
import { ExpenseIdentifier } from '@models/expense-identifier';
import { SetQuery } from '@models/queries/set-query';
import { RequestStatusDTO } from '@models/request';
import { StudyExpense, VisitName } from '@models/study';
import { StudyExpenseTrackingDataRowDto } from '@models/studyExpenseTrackingDataRow';
import { SiteService } from '@services/site/site.service';
import { MdsOptionGeneric } from '@utility/utility';
import { compare } from 'fast-json-patch';
import { Observable, of } from 'rxjs';

@Injectable({
    providedIn: 'root',
})
export class ExpenseTrackingService {
    public readonly timeToShowLoadingIndicatorMs = 200;
    http = inject(HttpClient);
    siteService = inject(SiteService);

    getStudyCountries(studyId: Signal<number>) {
        return httpResource<CountryRegionCtsmViewModel[]>(
            () => `${this.siteService.getSiteBaseUrl()}/api/studies/${studyId()}/countries`
        );
    }
    getStudyVisitNames(studyId: Signal<number>) {
        return httpResource<VisitName[]>(
            () => `${this.siteService.getSiteBaseUrl()}/api/studies/${studyId()}/visitnames`
        );
    }
    getStudyExpenseTypes(studyId: Signal<number>) {
        return httpResource<StudyExpense[]>(
            () => `${this.siteService.getSiteBaseUrl()}/api/studies/${studyId()}/expensetypes`
        );
    }

    //new endpoints in new partial controller
    public readonly paymentMerchants = httpResource<MdsOptionGeneric<number>[]>(
        () => `${this.siteService.getSiteBaseUrl()}/api/v2/dictionaries/paymentRequestMerchantTypes`
    );

    //new endpoints in new partial controller
    public readonly travelMerchants = httpResource<MdsOptionGeneric<number>[]>(
        () => `${this.siteService.getSiteBaseUrl()}/api/v2/dictionaries/travelRequestMerchantTypes`
    );

    getAvailableActionsDictionary() {
        return httpResource<{
            travelActionsDictionary: { [key: number]: number[] };
            paymentActionsDictionary: { [key: number]: number[] };
        }>(() => `${this.siteService.getSiteBaseUrl()}/api/v2/dictionaries/available-statuses`);
    }

    getCurrencies() {
        return httpResource<CurrencyViewModel[]>(() => `${this.siteService.getSiteBaseUrl()}/api/currencies`);
    }

    getByExpenseIdentifiers(expenseIdentifiers: ExpenseIdentifier[]) {
        return this.http.post<StudyExpenseTrackingDataRowDto[]>(
            `${this.siteService.getSiteBaseUrl()}/api/expense-tracking/by-ids`,
            expenseIdentifiers
        );
    }

    patchBatchExpenses(studyId: number, expenses: BatchEditSetDataRow[]) {
        const patchData = expenses
            .map((expense) => ({
                expenseId: expense.expenseId,
                requestType: expense.requestType,
                rowData: compare(expense.oldRow, expense.newRow),
            }))
            .filter((e) => e.rowData.length > 0);

        if (patchData.length === 0) {
            return of(0);
        }

        return this.http.patch<number>(
            `${this.siteService.getSiteBaseUrl()}/api/expense-tracking/${studyId}/batch`,
            patchData
        );
    }

    checkForDuplicates(expenses: BatchEditSetDataRow[]): Observable<DuplicateCheckResultDto> {
        const patchData = expenses
            .map((expense) => ({
                expenseId: expense.expenseId,
                requestType: expense.requestType,
                requestId: expense.requestId,
                rowData:
                    expense.expenseId === 0
                        ? compare({} as typeof expense.newRow, expense.newRow)
                        : compare(expense.oldRow, expense.newRow),
            }))
            .filter((e) => e.rowData.length > 0);

        if (patchData.length === 0) {
            return of({ hasDuplicates: false, potentialDuplicates: [] });
        }

        return this.http.post<DuplicateCheckResultDto>(
            `${this.siteService.getSiteBaseUrl()}/api/expense-tracking/check-duplicates`,
            patchData
        );
    }

    createReimbursementExpenses(newExpenses: BatchSetAddExpense[]): Observable<BatchSetAddResponse[]> {
        if (newExpenses.length === 0) {
            return of([]);
        }

        return this.http.post<BatchSetAddResponse[]>(
            `${this.siteService.getSiteBaseUrl()}/api/expense-tracking/reimbursement/new-expenses`,
            newExpenses
        );
    }

    CompleteReimbursement(requestStatusDto: RequestStatusDTO) {
        return this.http.put<number>(
            `${this.siteService.getSiteBaseUrl()}/api/expense-tracking/reimbursement/complete`,
            requestStatusDto
        );
    }

    CompleteStipend(requestStatusDto: RequestStatusDTO) {
        return this.http.put<number>(
            `${this.siteService.getSiteBaseUrl()}/api/expense-tracking/stipend/complete`,
            requestStatusDto
        );
    }

    CompleteTravel(requestStatusDto: RequestStatusDTO) {
        return this.http.put<number>(
            `${this.siteService.getSiteBaseUrl()}/api/expense-tracking/travel/complete`,
            requestStatusDto
        );
    }

    getDataToExport(studyId: number): Observable<StudyExpenseTrackingDataRowDto[]> {
        return this.http.get<StudyExpenseTrackingDataRowDto[]>(
            `${this.siteService.getSiteBaseUrl()}/api/expense-tracking/${studyId}/export`
        );
    }

    getData(studyId: number, setQuery: SetQuery): Observable<StudyExpenseTrackingDataRowDto[]> {
        return this.http.post<StudyExpenseTrackingDataRowDto[]>(
            `${this.siteService.getSiteBaseUrl()}/api/expense-tracking/${studyId}/filtered`,
            setQuery
        );
    }

    getDataByRequest(
        studyId: number,
        requestType: string,
        requestId: Number
    ): Observable<StudyExpenseTrackingDataRowDto[]> {
        return this.http.get<StudyExpenseTrackingDataRowDto[]>(
            `${this.siteService.getSiteBaseUrl()}/api/expense-tracking/studies/${studyId}/request/${requestType}/${requestId}`
        );
    }

    batchConvertAuthorizedAmount(
        batch: ConvertAuthorizedAmountRequest[]
    ): Observable<ConvertAuthorizedAmountResponse[]> {
        if (batch.length === 0) {
            return of([]);
        }

        return this.http.post<ConvertAuthorizedAmountResponse[]>(
            `${this.siteService.getSiteBaseUrl()}/api/expense-tracking/batch/convert`,
            batch
        );
    }
}
